import React, { useState, useEffect, useCallback } from 'react';
import { assignmentAPI } from '../utils/api';
import { AlertTriangle, Phone, X, Check } from 'lucide-react';
import { toast } from 'sonner';

const PendingConfirmationsAlert = () => {
  const [items, setItems] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [busy, setBusy] = useState(null); // assignment_id currently being confirmed

  const fetchPending = useCallback(async () => {
    try {
      const r = await assignmentAPI.pendingConfirmations();
      setItems(r.data || []);
    } catch (e) {
      // silent — this is an ambient widget, not critical
    }
  }, []);

  useEffect(() => { fetchPending(); }, [fetchPending]);

  const handleMarkWorked = async (a) => {
    setBusy(a.assignment_id);
    try {
      await assignmentAPI.markWorked(a.assignment_id);
      toast.success(`${a.crew_member_name} marked as confirmed`);
      fetchPending();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update');
    } finally {
      setBusy(null);
    }
  };

  if (items.length === 0) return null;

  return (
    <div className="border-l-4 border-amber-500 bg-amber-50 dark:bg-amber-500/10 p-4 mb-6 dark:bg-amber-950/30 dark:border-amber-600" data-testid="pending-confirmations-alert">
      <div className="flex items-start gap-3">
        <AlertTriangle size={20} className="text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <p className="text-sm font-medium text-amber-900 dark:text-amber-200">
              {items.length} assignment{items.length !== 1 ? 's' : ''} awaiting crew confirmation
            </p>
            <button
              onClick={() => setExpanded(v => !v)}
              className="text-xs text-amber-800 dark:text-amber-300 hover:text-amber-900 dark:text-amber-300 underline underline-offset-2 dark:text-amber-300"
              data-testid="pending-confirmations-toggle"
            >
              {expanded ? 'Hide' : 'View'}
            </button>
          </div>
          {expanded && (
            <div className="mt-3 space-y-2">
              {items.map(a => (
                <div
                  key={a.assignment_id}
                  className="flex flex-wrap items-center gap-2 sm:gap-3 p-2 bg-card/60 dark:bg-amber-950/50 rounded"
                  data-testid={`pending-row-${a.assignment_id}`}
                >
                  <div className="flex-1 min-w-[180px]">
                    <p className="text-sm font-medium">{a.crew_member_name}</p>
                    <p className="text-xs text-muted-foreground">
                      {a.event_name} · {a.event_date}{a.start_time ? ` · ${a.start_time}` : ''}
                      {a.hours_since_assigned != null && (
                        <span className="ml-2 text-amber-700 dark:text-amber-400">· {a.hours_since_assigned}h without response</span>
                      )}
                    </p>
                  </div>
                  <div className="flex gap-1.5">
                    {a.crew_member_phone && (
                      <a
                        href={`tel:${a.crew_member_phone}`}
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs border border-border hover:bg-accent"
                        data-testid={`call-${a.assignment_id}`}
                      >
                        <Phone size={12} /> Call
                      </a>
                    )}
                    <button
                      onClick={() => handleMarkWorked(a)}
                      disabled={busy === a.assignment_id}
                      className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
                      data-testid={`mark-worked-${a.assignment_id}`}
                      title="Mark as accepted retroactively (they've worked / are working the event)"
                    >
                      <Check size={12} /> Confirmed
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PendingConfirmationsAlert;
